import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { Assessment_Status_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ownerContributorAndViewerIds } from 'src/components/form';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import { useUpdateAssessment } from 'src/hooks/mutations/assessment/useUpdateAssessment';
import { useGetAssessmentById } from 'src/hooks/queries';
import AssessmentForm from 'src/pages/assessments/forms/assessment-form';
import type { AssessmentFormDataFields } from 'src/pages/assessments/forms/assessment-form/assessmentSchema';
import { defaultValues } from 'src/pages/assessments/forms/assessment-form/assessmentSchema';
import {
  getContributors,
  getOwners,
  getViewers,
} from 'src/rbac/contributorHelper';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';
import { assessmentDetailsUrl } from 'src/utils/urls';

import { useGetAssessmentRCSAActivitiesByAssessmentId } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import AssessmentFindingPreview from '../../../forms/assessment-findings-preview/AssessmentFindingPreview';
import AssessmentStatusPreview from '../../../forms/assessment-status-preview/AssessmentStatusPreview';

const Tab: FC = () => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'assessmentActivities',
  });
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const assessmentId = useGetGuidParam('assessmentId');
  const navigate = useNavigate();
  const {
    data,
    error,
    loading: loadingAssessment,
  } = useGetAssessmentById({
    queryArgs: { Id: assessmentId },
    shouldSkip: !assessmentId,
  });
  if (error) {
    throw error;
  }

  const assessment = data?.assessment[0];
  const { hasPermission: canEdit, loading: canEditLoading } =
    useHasPermissionQuery('update:assessment', assessment);
  const { updateAssessment: updateMutationFn } = useUpdateAssessment();

  const { data: activitiesRCSAData } =
    useGetAssessmentRCSAActivitiesByAssessmentId({
      queryArgs: { assessmentId },
    });

  const disabledUsers = activitiesRCSAData?.assessment_activity.map(
    (activity) => ({
      userId: activity.AssignedUser ?? '',
      reason: t('disabledUserPrompt'),
    })
  );

  const onSave = async (data: AssessmentFormDataFields) => {
    if (!assessment) {
      throw new Error('Missing assessment');
    }
    await updateMutationFn({
      Id: assessment.Id,
      // Optimistic-concurrency token: the snapshot this form was loaded from.
      // A second editor saving over a newer row is rejected with a 409 rather
      // than silently winning (COI-622).
      OriginalTimestamp: assessment.ModifiedAtTimestamp,
      OriginatingItemId: assessment.OriginatingItemId,
      CustomAttributeData: data.CustomAttributeData || undefined,
      ...ownerContributorAndViewerIds(data),
      TagTypeIds: data.tags?.map((t) => t.TagTypeId) || [],
      DepartmentTypeIds: data.departments?.map((d) => d.DepartmentTypeId) || [],
      CompletedByUser: data.CompletedByUser?.value ?? null,
      Status: data.Status ?? Assessment_Status_Enum.Notstarted,
      StartDate: data.StartDate,
      Title: data.Title,
      Summary: data.Summary,
      ActualCompletionDate: data.ActualCompletionDate,
      NextTestDate: data.NextTestDate,
      TargetCompletionDate: data.TargetCompletionDate,
      Outcome: data.Outcome,
    });
    navigate(assessmentDetailsUrl(assessmentId), { replace: true });
  };

  const onDismiss = (saved?: boolean) => {
    if (!saved) {
      navigate(-1);
    }
  };

  return (
    <AssessmentForm
      readOnly={!canEdit || canEditLoading || loadingAssessment}
      values={{
        ...defaultValues,
        ...assessment,
        Summary: assessment?.Summary ?? '',
        Owners: getOwners(assessment),
        Contributors: getContributors(assessment),
        Viewers: getViewers(assessment),
        ActualCompletionDate: assessment?.ActualCompletionDate ?? null,
        NextTestDate: assessment?.NextTestDate ?? null,
        TargetCompletionDate: assessment?.TargetCompletionDate ?? null,
        StartDate: assessment?.StartDate ?? null,
        CompletedByUser: assessment?.CompletedByUser
          ? { value: assessment.CompletedByUser, type: 'user' }
          : null,
        ancestorContributors: assessment?.ancestorContributors ?? [],
        Status: assessment?.Status ?? 'notstarted',
      }}
      onSave={onSave}
      onDismiss={onDismiss}
      publishToolbarState={objectToolbarEnabled}
      aside={
        <>
          <AssessmentStatusPreview
            status={assessment?.Status}
            targetCompletionDate={assessment?.TargetCompletionDate}
            actualCompletionDate={assessment?.ActualCompletionDate}
          />
          <AssessmentFindingPreview
            assessmentId={assessment?.Id}
            outcome={assessment?.Outcome}
          />
        </>
      }
      renderTemplate={(renderProps) => <PageWrapper {...renderProps} />}
      disabledUsers={disabledUsers}
    />
  );
};

export default Tab;
